import React, { useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useAsync, UseAsyncReturn } from 'react-async-hook'

import { Customer, Asset } from '../../api/types'
import { Filter, FiltersAsParams } from './types'
import countryCodes from './CountryCodes'
import useAPIClient from '../../hooks/useAPIClient'
import states from './states'
import productTypes from '../../constants/productTypes'

const FILTER_TYPES = {
  COUNTRY: 'country',
  STATE: 'state',
  PARTNER_NAME: 'partnerName',
  CUSTOMER: 'customer',
  CUSTOM: 'custom',
  PRODUCT_TYPE: 'productType',
}

const orderCustomersByName = ({ asc }: { asc: boolean }) => {
  const sortAsc = (customers: Customer[]): Customer[] => {
    return customers.slice().sort((a, b) => {
      const aName = a.accountname.toLowerCase()
      const bName = b.accountname.toLowerCase()
      return aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true })
    })
  }

  const sortDesc = (customers: Customer[]): Customer[] => {
    return sortAsc(customers).reverse()
  }
  return asc ? sortAsc : sortDesc
}

// TODO: Remove ignore when/if this function is used and tested.
// istanbul ignore next
const orderAssetsByDescription = ({ asc }: { asc: boolean }) => {
  const sortFunction = (groupedAssets: AssetsGroupedByOrderNoType): AssetsGroupedByOrderNoType => {
    const sortedObject: AssetsGroupedByOrderNoType = {}
    let sortedObjectAsArray = Object.keys(groupedAssets).sort()
    sortedObjectAsArray = asc ? sortedObjectAsArray : sortedObjectAsArray.reverse()
    sortedObjectAsArray.forEach((key) => {
      sortedObject[key] = groupedAssets[key]
    })
    return sortedObject
  }

  return sortFunction
}

const useFilters = () => {
  const [filters, setFilters] = React.useState<Filter[]>([])

  const handleDeleteFilter = (filterToBeDeleted: Filter) => {
    setFilters((currentFilters) =>
      currentFilters.filter(
        (filter) =>
          !(filterToBeDeleted.filter === filter.filter && filterToBeDeleted.type === filter.type),
      ),
    )
  }

  const handleAddFilter = (filter: Filter) => {
    setFilters((currentFilters) => {
      const isAlreadySelected = Boolean(
        filters.find((f) => f.filter === filter.filter && f.type === filter.type),
      )
      if (!isAlreadySelected) {
        return [...currentFilters, filter]
      }
      return currentFilters
    })
  }

  const clearFilters = () => {
    setFilters([])
  }

  return { filters, handleDeleteFilter, handleAddFilter, setFilters, clearFilters }
}

function groupFilters(filters: Filter[]): Filter[][] {
  const filterTypes = filters.reduce<string[]>(
    (acc, next) => (acc.includes(next.type) ? acc : [...acc, next.type]),
    [],
  )
  return filterTypes.reduce<Filter[][]>((acc, filterType) => {
    const filterGroup = filters.filter((filter) => filter.type === filterType)
    return filterGroup.length > 0 ? [...acc, filterGroup] : acc
  }, [])
}

const prepareFilters = (filters: Filter[]): FiltersAsParams => {
  const param: FiltersAsParams = {}
  groupFilters(filters).forEach((filterGroup) => {
    filterGroup.forEach((filter) => {
      switch (filter.type) {
        case FILTER_TYPES.COUNTRY:
          const countrycode = Object.keys(countryCodes).find(
            (code) => countryCodes[code] === filter.filter,
          )
          if (countrycode) param.countrycode = countrycode
          break
        case FILTER_TYPES.STATE:
          param.state = filter.filter
          break
        default:
          param.search = `${filter.type}:${filter.filter}`
          break
      }
    })
  })

  return param
}

const prepareAssetsFiltersAsParams = (filters: Filter[]): FiltersAsParams => {
  const filtersAsParams: FiltersAsParams = {}
  filters.forEach(({ filter, type }) => {
    switch (type) {
      case FILTER_TYPES.COUNTRY:
      case FILTER_TYPES.STATE:
      case FILTER_TYPES.CUSTOMER:
      case FILTER_TYPES.CUSTOM:
        filtersAsParams[`labels.${type}`] = filter
        break
      case FILTER_TYPES.PRODUCT_TYPE:
        filtersAsParams[type] = productTypes[filter]
        break
      default:
        filtersAsParams[type] = filter
        break
    }
  })

  return filtersAsParams
}

const useFiltersUrlSync = (filters: Filter[], setFilters: Function) => {
  const history = useHistory()
  const { search, pathname } = useLocation()
  const syncFiltersToUrl = () => {
    const searchParams = new URLSearchParams()
    filters.forEach((filter) => {
      const filterQueryValue = `${filter.type}:${filter.filter}`
      searchParams.append('filter', filterQueryValue)
    })

    const newSearchParams = searchParams.toString()
    if (newSearchParams !== search) {
      history.replace(`${pathname}?${newSearchParams}`)
    }
  }

  const initFiltersFromUrl = () => {
    const searchParams = new URLSearchParams(search)
    const queryParamFilters: Filter[] = searchParams.getAll('filter').map((value) => {
      const [filterType, filterName] = value.split(':') as [keyof Asset, string]
      return { filter: filterName, type: filterType }
    })
    setFilters(queryParamFilters)
  }

  return { initFiltersFromUrl, syncFiltersToUrl }
}

export type Label = {
  label: string
  category: string
  id?: string
}

type UseLabelAutocomplete = {
  (customerId: string, category: string, dependency?: Label[] | undefined): [
    UseAsyncReturn<Label[], any[]>,
    React.Dispatch<React.SetStateAction<string>>,
  ]
}

const useLabelAutocomplete: UseLabelAutocomplete = (customerId, category, dependency) => {
  const [inputText, setInputText] = useState('')
  const apiClient = useAPIClient()
  const asyncLabels = useAsync<Label[]>(async () => {
    const { data } = await apiClient.listLabelsByCustomerId(customerId, category)
    return data
  }, [dependency])

  if (inputText && asyncLabels.status === 'success') {
    // Case-insensitive search
    const filteredLabels = asyncLabels.result?.filter((l) =>
      l.label.toLowerCase().includes(inputText.toLowerCase()),
    )
    asyncLabels.result = filteredLabels
  }

  return [asyncLabels, setInputText]
}

const getStates = (country: string | undefined): string[] => {
  if (!country || !['united states', 'china', 'australia', 'canada'].includes(country)) {
    return []
  }
  return states[country]
}
export interface AssetsGroupedByOrderNoType {
  [key: string]: [Asset]
}

const groupedBySalesOrderNo = (
  assets: Asset[],
  fallbackToSerialNumber = false,
): AssetsGroupedByOrderNoType => {
  let assetsBySalesOrder: AssetsGroupedByOrderNoType = {}
  assets?.forEach((item) => {
    let orderNumber = item.SalesOrderNo!
    if (fallbackToSerialNumber && !orderNumber) {
      orderNumber = item.InternalSalesOrderNo
        ? `IO-${item.InternalSalesOrderNo}`
        : `SN-${item.SerialNumber}`
    }
    if (!(orderNumber in assetsBySalesOrder)) {
      assetsBySalesOrder[orderNumber] = [item]
    } else {
      assetsBySalesOrder[orderNumber].push(item)
    }
  })
  return assetsBySalesOrder
}

const getYearRange = (maxRange: number = 10): string[] => {
  const currentYear = new Date().getUTCFullYear()

  return Array(currentYear - (currentYear - maxRange))
    .fill('')
    .map((v, idx) => `${currentYear - idx}`) as Array<string>
}

export {
  orderAssetsByDescription,
  orderCustomersByName,
  useFilters,
  useFiltersUrlSync,
  useLabelAutocomplete,
  prepareFilters,
  prepareAssetsFiltersAsParams,
  getStates,
  groupFilters,
  groupedBySalesOrderNo,
  getYearRange,
}
